<template>
  <div class="upload-form">
    <div 
      class="card"
      :class="{ 'drag-over': isDragOver }"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <div class="card-body">
        <h6 class="card-title">
          <i class="bi bi-cloud-upload"></i>
          Nahrát nový obrázek
        </h6>
        
        <!-- Drag and drop area -->
        <div 
          v-if="!selectedFile"
          class="drag-drop-area"
          @click="triggerFileInput"
        >
          <div class="drag-drop-content">
            <i class="bi bi-cloud-upload-fill drag-drop-icon"></i>
            <p class="mb-2">
              <strong>Přetáhněte obrázek sem</strong>
            </p>
            <p class="text-muted mb-0">
              nebo klikněte pro výběr souboru
            </p>
            <small class="text-muted">
              Povolené formáty: JPG, PNG, BMP, TIFF, max. 6MB
            </small>
          </div>
        </div>
        
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <!-- File input -->
            <div class="col-md-6 mb-3">
              <label for="fileInput" class="form-label">
                Vyberte obrázek
              </label>
              <!-- Odstraněn required atribut -->
              <input
                id="fileInput"
                ref="fileInput"
                type="file"
                class="form-control"
                accept=".jpg,.jpeg,.png,.bmp,.tiff,.tif"
                @change="handleFileChange"
                :disabled="isUploading"
                style="display: none;"
              >
              
              <!-- Visible file input replacement -->
              <div 
                class="form-control file-input-display"
                @click="triggerFileInput"
                :class="{ 'disabled': isUploading }"
              >
                <span v-if="selectedFile" class="text-dark">
                  {{ selectedFile.name }}
                </span>
                <span v-else class="text-muted">
                  Klikněte pro výběr souboru nebo přetáhněte...
                </span>
                <i class="bi bi-folder2-open ms-2"></i>
              </div>
              
              <div class="form-text">
                Povolené formáty: JPG, PNG, BMP, TIFF, max. 6MB
              </div>
            </div>
            
            <!-- Description input -->
            <div class="col-md-6 mb-3">
              <label for="description" class="form-label">
                Popis obrázku
              </label>
              <textarea
                id="description"
                v-model="description"
                class="form-control"
                rows="3"
                placeholder="Zadejte popis obrázku..."
                :disabled="isUploading"
                v-validate="{
                     rules: { required: true},
                     message: { required: 'Popis obrázku je povinny' },
                     options: { showValidState: true }
                   }"
                maxlength="500"
              ></textarea>
              <div class="form-text">
                {{ description.length }}/500 znaků
              </div>
            </div>
          </div>
          
          <!-- File preview -->
          <div v-if="previewUrl" class="mb-3">
            <div class="row">
              <div class="col-auto">
                <img 
                  :src="previewUrl" 
                  alt="Náhled"
                  class="img-thumbnail"
                  style="max-width: 150px; max-height: 150px;"
                >
              </div>
              <div class="col">
                <p class="mb-1"><strong>{{ selectedFile?.name }}</strong></p>
                <p class="mb-0 text-muted">
                  Velikost: {{ formatFileSize(selectedFile?.size) }}
                </p>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger mt-2"
                  @click="removeFile"
                  :disabled="isUploading"
                >
                  <i class="bi bi-trash"></i>
                  Odstranit
                </button>
              </div>
            </div>
          </div>
          
          <!-- Submit button -->
          <div class="d-flex justify-content-end">
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="!selectedFile || !description.trim() || isUploading"
            >
              <span v-if="isUploading" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="bi bi-upload me-2"></i>
              {{ isUploading ? 'Nahrávám...' : 'Nahrát obrázek' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue'

const emit = defineEmits(['upload', 'error'])

const props = defineProps({
  isUploading: {
    type: Boolean,
    default: false
  }
})

const fileInput = ref(null)
const selectedFile = ref(null)
const description = ref('')
const previewUrl = ref('')
const isDragOver = ref(false)

// Drag and drop handlers
const handleDragOver = (event) => {
  event.preventDefault()
  isDragOver.value = true
}

const handleDragLeave = (event) => {
  event.preventDefault()
  // Zkontroluj, zda opouštíme celou komponentu
  if (!event.currentTarget.contains(event.relatedTarget)) {
    isDragOver.value = false
  }
}

const handleDrop = (event) => {
  event.preventDefault()
  isDragOver.value = false
  
  if (props.isUploading) return
  
  const files = event.dataTransfer.files
  if (files.length > 0) {
    const file = files[0]
    processFile(file)
  }
}

// Trigger file input click
const triggerFileInput = () => {
  if (!props.isUploading && fileInput.value) {
    fileInput.value.click()
  }
}

// File validation
const validateFile = (file) => {
  const allowedTypes = [
    'image/jpeg', 
    'image/jpg', 
    'image/png', 
    'image/bmp', 
    'image/tiff', 
    'image/tif'
  ]
  const maxSize = 6 * 1024 * 1024 // 6MB
  
  if (!allowedTypes.includes(file.type)) {
    emit('error', 'Nepovolený formát souboru. Povolené jsou pouze JPG, PNG, BMP a TIFF obrázky.')
    return false
  }
  
  if (file.size > maxSize) {
    emit('error', 'Soubor je příliš velký. Maximální velikost je 6MB.')
    return false
  }
  
  return true
}

// Process file (for both drag-drop and file input)
const processFile = (file) => {
  if (!validateFile(file)) {
    return
  }
  
  selectedFile.value = file
  
  // Pro drag & drop musíme také nastavit value file inputu
  // Vytvořím DataTransfer objekt pro synchronizaci
  const dataTransfer = new DataTransfer()
  dataTransfer.items.add(file)
  if (fileInput.value) {
    fileInput.value.files = dataTransfer.files
  }
  
  // Vytvoření náhledu
  const reader = new FileReader()
  reader.onload = (e) => {
    previewUrl.value = e.target.result
  }
  reader.onerror = () => {
    emit('error', 'Chyba při čtení souboru.')
  }
  reader.readAsDataURL(file)
}

const handleFileChange = (event) => {
  const file = event.target.files[0]
  if (file) {
    processFile(file)
  } else {
    removeFile()
  }
}

const removeFile = () => {
  selectedFile.value = null
  previewUrl.value = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const handleSubmit = () => {
  // Vlastní validace před odesláním
  if (!selectedFile.value) {
    emit('error', 'Musíte vybrat obrázek.')
    return
  }
  
  if (!description.value.trim()) {
    emit('error', 'Popis obrázku je povinný.')
    return
  }
  
  emit('upload', {
    file: selectedFile.value,
    description: description.value.trim()
  })
  
  // Reset formuláře
  resetForm()
}

const resetForm = () => {
  selectedFile.value = null
  description.value = ''
  previewUrl.value = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B'
  
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

// Reset formuláře po dokončení uploadu
watch(() => props.isUploading, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    // Upload byl dokončen
    setTimeout(resetForm, 100)
  }
})
</script>

<style scoped>
.upload-form .card {
  border: 2px dashed #dee2e6;
  transition: all 0.15s ease-in-out;
}

.upload-form .card:hover {
  border-color: #0d6efd;
}

.upload-form .card.drag-over {
  border-color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.05);
  transform: scale(1.02);
}

.drag-drop-area {
  padding: 2rem;
  text-align: center;
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.drag-drop-area:hover {
  border-color: #0d6efd;
  background-color: rgba(13, 110, 253, 0.05);
}

.drag-drop-content {
  pointer-events: none;
}

.drag-drop-icon {
  font-size: 3rem;
  color: #6c757d;
  margin-bottom: 1rem;
}

.file-input-display {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 38px;
}

.file-input-display:hover:not(.disabled) {
  border-color: #0d6efd;
}

.file-input-display.disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.img-thumbnail {
  object-fit: cover;
}
</style>